import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Store, Package, Link2, Zap, Settings, LogOut, Plus, Trash2, RefreshCw,
  ExternalLink, ShoppingBag, BarChart3, Loader2, Search, Globe, Key,
  ArrowLeft, ToggleLeft, ToggleRight, Edit, X, Check, Upload, Bell, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  amazon: 'bg-orange-100 text-orange-700 border-orange-200',
  walmart: 'bg-blue-100 text-blue-700 border-blue-200',
  ebay: 'bg-red-100 text-red-700 border-red-200',
  temu: 'bg-purple-100 text-purple-700 border-purple-200',
  facebook_marketplace: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  shopify: 'bg-green-100 text-green-700 border-green-200',
  custom: 'bg-gray-100 text-gray-700 border-gray-200'
};

const PLATFORM_ICONS = {
  amazon: '🛒', walmart: '🏪', ebay: '🏷️', temu: '📦',
  facebook_marketplace: '📘', shopify: '🛍️', custom: '🌐'
};

export default function VendorDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('vendor_token');
  const [activeTab, setActiveTab] = useState('overview');
  const [vendor, setVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Webhook config
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);

  // Connect store dialog
  const [showConnect, setShowConnect] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState('');
  const [connectFields, setConnectFields] = useState({});
  const [connectName, setConnectName] = useState('');
  const [connectAutoSync, setConnectAutoSync] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  // Import URL dialog
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importTitle, setImportTitle] = useState('');
  const [importPrice, setImportPrice] = useState('');
  const [importCategory, setImportCategory] = useState('general');
  const [importLoading, setImportLoading] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) { navigate('/vendor/login'); return; }
    try {
      const [vendorRes, statsRes, storesRes, productsRes, platformsRes, notifsRes, webhookRes] = await Promise.all([
        fetch(`${API}/vendor/me`, { headers }),
        fetch(`${API}/vendor/dashboard/stats`, { headers }),
        fetch(`${API}/vendor/stores`, { headers }),
        fetch(`${API}/vendor/products`, { headers }),
        fetch(`${API}/vendor/platforms`),
        fetch(`${API}/vendor/notifications`, { headers }),
        fetch(`${API}/vendor/webhook`, { headers })
      ]);
      if (!vendorRes.ok) { localStorage.removeItem('vendor_token'); navigate('/vendor/login'); return; }
      setVendor(await vendorRes.json());
      const statsData = await statsRes.json();
      setStats(statsData);
      setStores((await storesRes.json()).stores);
      setProducts((await productsRes.json()).products);
      setPlatforms((await platformsRes.json()).platforms);
      const notifsData = await notifsRes.json();
      setNotifications(notifsData.notifications || []);
      setUnreadCount(notifsData.unread_count || 0);
      const wh = await webhookRes.json();
      setWebhookUrl(wh.webhook_url || '');
      setWebhookEnabled(wh.webhook_enabled || false);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem('vendor_token');
    navigate('/vendor/login');
  };

  const handleConnect = async () => {
    if (!connectPlatform) return;
    setConnectLoading(true);
    try {
      const body = { platform: connectPlatform, store_name: connectName || platforms[connectPlatform]?.name, auto_sync: connectAutoSync, ...connectFields };
      const res = await fetch(`${API}/vendor/stores/connect`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(data.message);
      setShowConnect(false);
      setConnectPlatform('');
      setConnectFields({});
      setConnectName('');
      fetchAll();
    } catch (err) { toast.error(err.message); }
    finally { setConnectLoading(false); }
  };

  const handleDisconnect = async (storeId) => {
    if (!window.confirm('Disconnect this store? Products already imported will remain.')) return;
    try {
      const res = await fetch(`${API}/vendor/stores/${storeId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Store disconnected'); fetchAll(); }
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleSync = async (storeId) => {
    setSyncing(storeId);
    try {
      const res = await fetch(`${API}/vendor/stores/${storeId}/sync`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(data.message);
      fetchAll();
    } catch (err) { toast.error(err.message); }
    finally { setSyncing(null); }
  };

  const handleToggleSync = async (storeId) => {
    try {
      const res = await fetch(`${API}/vendor/stores/${storeId}/toggle-sync`, { method: 'PUT', headers });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.auto_sync_enabled ? 'Auto-sync enabled' : 'Auto-sync disabled');
        fetchAll();
      }
    } catch { toast.error('Failed to toggle sync'); }
  };

  const handleImportUrl = async () => {
    if (!importUrl) { toast.error('Enter a product URL'); return; }
    setImportLoading(true);
    try {
      const body = { url: importUrl, title: importTitle || undefined, price: importPrice ? parseFloat(importPrice) : undefined, category: importCategory };
      const res = await fetch(`${API}/vendor/products/import-url`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Imported: ${data.title}`);
      setShowImport(false);
      setImportUrl('');
      setImportTitle('');
      setImportPrice('');
      fetchAll();
    } catch (err) { toast.error(err.message); }
    finally { setImportLoading(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Remove this product from marketplace?')) return;
    try {
      const res = await fetch(`${API}/vendor/products/${productId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Product removed'); fetchAll(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveWebhook = async () => {
    setWebhookSaving(true);
    try {
      const res = await fetch(`${API}/vendor/webhook`, { method: 'PUT', headers, body: JSON.stringify({ webhook_url: webhookUrl, webhook_enabled: webhookEnabled }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Webhook settings saved');
    } catch (err) { toast.error(err.message); }
    finally { setWebhookSaving(false); }
  };

  const handleTestWebhook = async () => {
    setWebhookTesting(true);
    try {
      const res = await fetch(`${API}/vendor/webhook/test`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message);
      toast.success(data.message);
    } catch (err) { toast.error(err.message); }
    finally { setWebhookTesting(false); }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/vendor/notifications/read`, { method: 'PUT', headers, body: JSON.stringify({}) });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed to mark as read'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'stores', label: 'Linked Stores', icon: Link2 },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'notifications', label: `Notifications${unreadCount > 0 ? ` (${unreadCount})` : ''}`, icon: Bell },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/20 to-cyan-50/10" data-testid="vendor-dashboard">
      {/* Header */}
      <header className="glass-header border-b border-slate-200/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Store className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-800">{vendor?.business_name || 'Vendor Portal'}</h1>
              <p className="text-xs text-slate-400">{vendor?.email}</p>
            </div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs ml-2">Vendor</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="vendor-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Jiffys Home
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="vendor-logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 glass rounded-xl p-1 w-fit" data-testid="vendor-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`} data-testid={`vendor-tab-${tab.id}`}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6" data-testid="vendor-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up">
              <Card className="glass hover-lift transition-all"><CardContent className="p-4 text-center">
                <Link2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.linked_stores || 0}</p>
                <p className="text-xs text-slate-500">Linked Stores</p>
              </CardContent></Card>
              <Card className="glass hover-lift transition-all"><CardContent className="p-4 text-center">
                <Package className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_products || 0}</p>
                <p className="text-xs text-slate-500">Total Products</p>
              </CardContent></Card>
              <Card className="glass hover-lift transition-all"><CardContent className="p-4 text-center">
                <ShoppingBag className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.listed_on_marketplace || 0}</p>
                <p className="text-xs text-slate-500">On Marketplace</p>
              </CardContent></Card>
              <Card className="glass hover-lift transition-all"><CardContent className="p-4 text-center">
                <Zap className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.auto_sync_active || 0}</p>
                <p className="text-xs text-slate-500">Auto-Sync Active</p>
              </CardContent></Card>
            </div>

            {stats?.platform_breakdown && Object.keys(stats.platform_breakdown).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Products by Platform</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.platform_breakdown).map(([plat, count]) => (
                      <Badge key={plat} className={`${PLATFORM_COLORS[plat] || PLATFORM_COLORS.custom} text-sm px-3 py-1`}>
                        {PLATFORM_ICONS[plat]} {platforms[plat]?.name || plat}: {count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => { setActiveTab('stores'); setShowConnect(true); }}>
                <CardContent className="p-6 text-center">
                  <Plus className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
                  <p className="font-semibold">Connect a Store</p>
                  <p className="text-sm text-slate-500">Link Amazon, Walmart, eBay, Shopify & more</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-blue-300 transition-colors" onClick={() => { setActiveTab('products'); setShowImport(true); }}>
                <CardContent className="p-6 text-center">
                  <Upload className="h-10 w-10 mx-auto text-blue-500 mb-2" />
                  <p className="font-semibold">Import Product by URL</p>
                  <p className="text-sm text-slate-500">Paste any product link to import manually</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Stores Tab */}
        {activeTab === 'stores' && (
          <div className="space-y-4" data-testid="vendor-stores">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Linked Stores</h2>
              <Button onClick={() => setShowConnect(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-store-btn">
                <Plus className="h-4 w-4 mr-2" /> Connect Store
              </Button>
            </div>

            {stores.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-500">
                <Link2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="font-medium">No stores connected yet</p>
                <p className="text-sm">Click "Connect Store" to link your first platform</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4">
                {stores.map(store => (
                  <Card key={store.store_id} data-testid={`store-card-${store.store_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{PLATFORM_ICONS[store.platform] || '🌐'}</span>
                          <div>
                            <p className="font-semibold">{store.store_name}</p>
                            <Badge className={`${PLATFORM_COLORS[store.platform] || PLATFORM_COLORS.custom} text-xs`}>
                              {store.platform_name}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={store.auto_sync_enabled ? 'default' : 'outline'} className={store.auto_sync_enabled ? 'bg-emerald-100 text-emerald-700' : ''}>
                            {store.auto_sync_enabled ? 'Auto-Sync ON' : 'Manual'}
                          </Badge>
                          <span className="text-sm text-slate-500">{store.products_synced} products</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                        <Button size="sm" variant="outline" onClick={() => handleSync(store.store_id)} disabled={syncing === store.store_id} data-testid={`sync-btn-${store.store_id}`}>
                          {syncing === store.store_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                          Sync Now
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleSync(store.store_id)}>
                          {store.auto_sync_enabled ? <ToggleRight className="h-3 w-3 mr-1 text-emerald-600" /> : <ToggleLeft className="h-3 w-3 mr-1" />}
                          {store.auto_sync_enabled ? 'Disable Auto' : 'Enable Auto'}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700 ml-auto" onClick={() => handleDisconnect(store.store_id)}>
                          <Trash2 className="h-3 w-3 mr-1" /> Disconnect
                        </Button>
                      </div>
                      {store.last_sync_at && (
                        <p className="text-xs text-slate-400 mt-2">Last synced: {new Date(store.last_sync_at).toLocaleString()}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-4" data-testid="vendor-products">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Products ({products.length})</h2>
              <Button onClick={() => setShowImport(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="import-url-btn">
                <Upload className="h-4 w-4 mr-2" /> Import by URL
              </Button>
            </div>

            {products.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-500">
                <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="font-medium">No products yet</p>
                <p className="text-sm">Connect a store and sync, or import products by URL</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-3">
                {products.map(product => (
                  <Card key={product.product_id} data-testid={`product-card-${product.product_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{product.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`${PLATFORM_COLORS[product.platform] || PLATFORM_COLORS.custom} text-xs`}>
                              {PLATFORM_ICONS[product.platform]} {product.platform}
                            </Badge>
                            <span className="text-xs text-slate-500">{product.import_method === 'auto_sync' ? 'Auto-synced' : 'Manual import'}</span>
                            {product.marketplace_listed && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                <Check className="h-3 w-3 mr-1" /> Listed
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-emerald-600">${product.price?.toFixed(2)}</p>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-7 px-2" onClick={() => handleDeleteProduct(product.product_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-4" data-testid="vendor-notifications">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Notifications {unreadCount > 0 && <Badge className="ml-2 bg-red-100 text-red-700">{unreadCount} new</Badge>}</h2>
              {notifications.length > 0 && (
                <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                  <Check className="h-3 w-3 mr-1" /> Mark All Read
                </Button>
              )}
            </div>

            {notifications.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-500">
                <Bell className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="font-medium">No notifications yet</p>
                <p className="text-sm">You'll see sale alerts and sync updates here</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {notifications.map(notif => (
                  <Card key={notif.notification_id} className={`transition-colors ${!notif.read ? 'border-l-4 border-l-emerald-500 bg-emerald-50/50' : ''}`}
                    data-testid={`notification-${notif.notification_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          notif.type === 'sale' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {notif.type === 'sale' ? <ShoppingBag className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm">{notif.title}</p>
                            {!notif.read && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5">{notif.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span>{new Date(notif.created_at).toLocaleString()}</span>
                            {notif.webhook_sent && (
                              <Badge variant="outline" className="text-xs">
                                <Send className="h-3 w-3 mr-1" />
                                Webhook {notif.webhook_status && notif.webhook_status < 400 ? 'Delivered' : 'Failed'}
                              </Badge>
                            )}
                            {notif.purchase_data?.platform && (
                              <Badge className={`${PLATFORM_COLORS[notif.purchase_data.platform] || PLATFORM_COLORS.custom} text-xs`}>
                                {PLATFORM_ICONS[notif.purchase_data.platform]} {notif.purchase_data.platform}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {notif.purchase_data?.price && (
                          <p className="font-bold text-emerald-600 text-lg shrink-0">${notif.purchase_data.price.toFixed(2)}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && vendor && (
          <div className="space-y-4" data-testid="vendor-settings">
            <h2 className="text-lg font-semibold">Vendor Settings</h2>
            <Card>
              <CardHeader><CardTitle className="text-base">Business Information</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs text-slate-500">Business Name</Label><p className="font-medium">{vendor.business_name}</p></div>
                <div><Label className="text-xs text-slate-500">Contact Name</Label><p>{vendor.contact_name}</p></div>
                <div><Label className="text-xs text-slate-500">Email</Label><p>{vendor.email}</p></div>
                <div><Label className="text-xs text-slate-500">Phone</Label><p>{vendor.phone || 'Not set'}</p></div>
                <div><Label className="text-xs text-slate-500">Website</Label><p>{vendor.website || 'Not set'}</p></div>
                <div><Label className="text-xs text-slate-500">Account Type</Label><Badge>{vendor.business_type}</Badge></div>
                <div><Label className="text-xs text-slate-500">Member Since</Label><p>{new Date(vendor.created_at).toLocaleDateString()}</p></div>
              </CardContent>
            </Card>

            {/* Webhook Configuration */}
            <Card data-testid="webhook-settings">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="h-4 w-4 text-blue-600" /> Webhook Notifications
                </CardTitle>
                <CardDescription>Get real-time POST notifications when your products sell on the Jiffys marketplace.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <button onClick={() => setWebhookEnabled(!webhookEnabled)}>
                    {webhookEnabled ? <ToggleRight className="h-6 w-6 text-emerald-600" /> : <ToggleLeft className="h-6 w-6 text-slate-400" />}
                  </button>
                  <div>
                    <p className="font-medium text-sm">{webhookEnabled ? 'Webhooks Enabled' : 'Webhooks Disabled'}</p>
                    <p className="text-xs text-slate-500">Receive POST requests with sale data when products sell</p>
                  </div>
                </div>
                <div>
                  <Label>Webhook URL</Label>
                  <Input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                    placeholder="https://your-server.com/api/jiffys-webhook" data-testid="webhook-url-input" />
                  <p className="text-xs text-slate-400 mt-1">We'll send a JSON POST with: event, product_title, price, buyer_name, purchase_id, platform, timestamp</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveWebhook} disabled={webhookSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-webhook-btn">
                    {webhookSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    Save Webhook
                  </Button>
                  {webhookUrl && (
                    <Button variant="outline" onClick={handleTestWebhook} disabled={webhookTesting} data-testid="test-webhook-btn">
                      {webhookTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Connect Store Dialog */}
      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent className="max-w-lg" data-testid="connect-store-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-emerald-600" /> Connect a Store</DialogTitle>
            <DialogDescription>Link your online store to auto-sync or manually import products.</DialogDescription>
          </DialogHeader>

          {!connectPlatform ? (
            <div className="grid grid-cols-2 gap-3 mt-2">
              {Object.entries(platforms).map(([key, plat]) => (
                <button key={key} onClick={() => setConnectPlatform(key)}
                  className={`p-4 rounded-lg border-2 border-transparent hover:border-emerald-300 bg-slate-50 hover:bg-emerald-50 transition-all text-center`}
                  data-testid={`platform-${key}`}>
                  <span className="text-2xl block mb-1">{PLATFORM_ICONS[key]}</span>
                  <p className="font-medium text-sm">{plat.name}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 mb-2">
                <button onClick={() => setConnectPlatform('')} className="text-slate-400 hover:text-slate-600"><ArrowLeft className="h-4 w-4" /></button>
                <span className="text-lg">{PLATFORM_ICONS[connectPlatform]}</span>
                <h3 className="font-semibold">{platforms[connectPlatform]?.name}</h3>
              </div>
              <div>
                <Label>Store Name (Optional)</Label>
                <Input value={connectName} onChange={e => setConnectName(e.target.value)} placeholder={`My ${platforms[connectPlatform]?.name} Store`} />
              </div>
              {platforms[connectPlatform]?.fields.map(field => (
                <div key={field}>
                  <Label className="flex items-center gap-1"><Key className="h-3 w-3" /> {platforms[connectPlatform]?.labels?.[field] || field} *</Label>
                  <Input type={field.includes('secret') || field.includes('token') || field.includes('key') ? 'password' : 'text'}
                    value={connectFields[field] || ''} onChange={e => setConnectFields({...connectFields, [field]: e.target.value})}
                    placeholder={`Enter ${platforms[connectPlatform]?.labels?.[field] || field}`} data-testid={`connect-field-${field}`} />
                </div>
              ))}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <button onClick={() => setConnectAutoSync(!connectAutoSync)} className="text-emerald-600">
                  {connectAutoSync ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6 text-slate-400" />}
                </button>
                <div>
                  <p className="font-medium text-sm">Enable Auto-Sync</p>
                  <p className="text-xs text-slate-500">Automatically pull new products every 24 hours</p>
                </div>
              </div>
              <Button onClick={handleConnect} disabled={connectLoading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-connect-btn">
                {connectLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                Connect {platforms[connectPlatform]?.name}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import URL Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent data-testid="import-url-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-blue-600" /> Import Product by URL</DialogTitle>
            <DialogDescription>Paste any product URL from Amazon, Walmart, eBay, Temu, Facebook, Shopify, or any website.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Product URL *</Label>
              <Input value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://www.amazon.com/dp/..." data-testid="import-url-input" />
            </div>
            <div>
              <Label>Custom Title (optional — AI will extract from URL)</Label>
              <Input value={importTitle} onChange={e => setImportTitle(e.target.value)} placeholder="Override product title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Custom Price (optional)</Label>
                <Input type="number" step="0.01" value={importPrice} onChange={e => setImportPrice(e.target.value)} placeholder="Override price" />
              </div>
              <div>
                <Label>Category</Label>
                <select className="w-full h-10 px-3 border rounded-md text-sm" value={importCategory} onChange={e => setImportCategory(e.target.value)}>
                  <option value="general">General</option>
                  <option value="auto_parts">Auto Parts</option>
                  <option value="tools">Tools</option>
                  <option value="electronics">Electronics</option>
                  <option value="home_garden">Home & Garden</option>
                  <option value="accessories">Accessories</option>
                </select>
              </div>
            </div>
            <Button onClick={handleImportUrl} disabled={importLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-import-btn">
              {importLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Import & List on Marketplace
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
